import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { failureKindOf } from './api';
import { ME_KEY } from './queries';

const RETRYABLE = new Set(['network', 'server']);

const identityChannel = new BroadcastChannel('portal-identity');

export function createQueryClient(): QueryClient {
  function sessionExpired(error: unknown): void {
    if (failureKindOf(error) !== 'unauthorized') return;
    void client.invalidateQueries({ queryKey: ME_KEY });
  }
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError(error, query) {
        if (query.queryKey[0] !== ME_KEY[0]) sessionExpired(error);
      },
    }),
    mutationCache: new MutationCache({ onError: sessionExpired }),
    defaultOptions: {
      queries: {
        staleTime: 15 * 60_000,
        gcTime: 60 * 60_000,
        retry: (count, error) =>
          count < 2 && RETRYABLE.has(failureKindOf(error) ?? 'unexpected'),
      },
    },
  });
  identityChannel.addEventListener('message', () => {
    void resetIdentity(client);
  });
  return client;
}

export async function refreshMember(client: QueryClient): Promise<void> {
  // Refill the Worker's member cache before dependent reads load the same caller.
  await client.invalidateQueries({ queryKey: ME_KEY, exact: true });
  await client.invalidateQueries({
    queryKey: ME_KEY,
    predicate: (query) => query.queryKey.length > ME_KEY.length,
  });
}

export async function switchIdentity(client: QueryClient): Promise<void> {
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel takes no target origin
  identityChannel.postMessage('switched');
  await resetIdentity(client);
}

async function resetIdentity(client: QueryClient): Promise<void> {
  await client.cancelQueries();
  client.removeQueries({
    predicate: (query) => query.queryKey[0] !== ME_KEY[0],
  });
  await client.resetQueries({ queryKey: ME_KEY });
}
